import { useCallback } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

export interface UseExcelShortcutsProps {
  // Selection
  onSelectAll?: () => void;
  onClearSelection?: () => void;
  
  // Editing
  onCopy?: () => void;
  onPaste?: () => void;
  onCut?: () => void;
  onDelete?: () => void;
  
  // Navigation
  onNavigate?: (direction: 'up' | 'down' | 'left' | 'right') => void;
  
  // Actions
  onNewRow?: () => void;
  onSave?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  
  // UI
  onToggleSidebar?: () => void;
  onSearch?: () => void;
}

export const useExcelShortcuts = ({
  onSelectAll,
  onClearSelection,
  onCopy,
  onPaste,
  onCut,
  onDelete,
  onNavigate,
  onNewRow,
  onSave,
  onUndo,
  onRedo,
  onToggleSidebar,
  onSearch
}: UseExcelShortcutsProps = {}) => {
  
  // Selection shortcuts
  useHotkeys('ctrl+a, cmd+a', (e) => {
    e.preventDefault();
    onSelectAll?.();
  });

  useHotkeys('escape', (e) => {
    e.preventDefault();
    onClearSelection?.();
  });

  // Editing shortcuts
  useHotkeys('ctrl+c, cmd+c', (e) => {
    e.preventDefault();
    onCopy?.();
  });

  useHotkeys('ctrl+v, cmd+v', (e) => {
    e.preventDefault();
    onPaste?.();
  });

  useHotkeys('ctrl+x, cmd+x', (e) => {
    e.preventDefault();
    onCut?.();
  });

  useHotkeys('delete, backspace', (e) => {
    e.preventDefault();
    onDelete?.();
  });

  // Navigation shortcuts
  useHotkeys('ctrl+arrowup, cmd+arrowup', (e) => {
    e.preventDefault();
    onNavigate?.('up');
  });

  useHotkeys('ctrl+arrowdown, cmd+arrowdown', (e) => {
    e.preventDefault();
    onNavigate?.('down');
  });

  useHotkeys('home', (e) => {
    e.preventDefault();
    onNavigate?.('left');
  });

  useHotkeys('end', (e) => {
    e.preventDefault();
    onNavigate?.('right');
  });

  // Action shortcuts
  useHotkeys('ctrl+n, cmd+n', (e) => {
    e.preventDefault();
    onNewRow?.();
  });

  useHotkeys('ctrl+s, cmd+s', (e) => {
    e.preventDefault();
    onSave?.();
  });

  useHotkeys('ctrl+z, cmd+z', (e) => {
    e.preventDefault();
    onUndo?.();
  });

  useHotkeys('ctrl+y, cmd+y, ctrl+shift+z, cmd+shift+z', (e) => {
    e.preventDefault();
    onRedo?.();
  });

  // UI shortcuts
  useHotkeys('ctrl+b, cmd+b', (e) => {
    e.preventDefault();
    onToggleSidebar?.();
  });

  useHotkeys('ctrl+f, cmd+f', (e) => {
    e.preventDefault();
    onSearch?.();
  });

  // Helper function to get current selection
  const getSelectedText = useCallback(() => {
    return window.getSelection()?.toString() || '';
  }, []);

  // Helper function to set clipboard data
  const setClipboardData = useCallback((text: string) => {
    navigator.clipboard.writeText(text).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  }, []);

  return {
    getSelectedText,
    setClipboardData
  };
};